/**
 * Dynamically loads and initializes the Airship SDK script.
 * Creates the necessary script injection for async SDK loading with provided configuration.
 *
 * @param sdkSrc - URL to the Airship SDK script
 * @param appKey - Airship application key
 * @param token - Authentication token for Airship services
 * @param vapidPublicKey - VAPID public key for push notifications
 * @param scriptId - Optional ID for the injected script element (defaults to '_uasdk')
 * @param workerUrl - URL to the push notification service worker (defaults to '/airship-push-worker.js')
 * @returns Promise that resolves to the Airship SDK instance
 */
export const loadAirshipScript = ({
  sdkSrc,
  appKey,
  token,
  vapidPublicKey,
  scriptId = "_uasdk",
  workerUrl = "/airship-push-worker.js",
}: {
  sdkSrc: string;
  appKey: string;
  token: string;
  vapidPublicKey: string;
  scriptId?: string;
  workerUrl?: string;
}) => {
  if (window?.UA) {
    return window.UA;
  }

  const scriptString = `(function (n, r, e, t, c) {
      var i,
        o = "Promise" in n,
        u = {
          then: function () { return u; },
          catch: function (n) { return n(new Error("Airship SDK Error: Unsupported browser")), u; },
        },
        s = o
          ? new Promise(function (n, r) {
              i = function (e, t) { e ? r(e) : n(t); };
            })
          : u;
      s._async_setup = function (n) {
        if (o)
          try { i(null, n(c)); } catch (n) { i(n); }
      };
      n[t] = s;
      var a = r.createElement("script");
      a.src = e; a.async = !0; a.id = "${scriptId}"; a.rel = t;
      r.head.appendChild(a);
    })(window, document, '${sdkSrc}', 'UA', {
      vapidPublicKey: '${vapidPublicKey}',
      appKey: '${appKey}',
      token: '${token}',
      workerUrl: '${workerUrl}',
      enabledFeatures: ['push', 'contacts']
    });`;

  try {
    const scriptElement = document.createElement("script");

    scriptElement.type = "text/javascript";
    scriptElement.textContent = scriptString;

    scriptElement.onerror = (error) => {
      console.error("[Airship] Script loading failed:", error);
    };

    scriptElement.onload = () => {};

    document.head.appendChild(scriptElement);

    return window.UA;
  } catch (error) {
    console.error(
      "[Airship] Failed to create or inject script element:",
      error,
    );

    throw error;
  }
};
